/**
 * `next/navigation` shim — the App Router hooks a copied component expects,
 * implemented on react-router. Same one-line-import rule as `compat/link`.
 *
 * Deliberate shape differences from Next, and why they are safe:
 *
 * - `useSearchParams()` returns a bare `URLSearchParams` (Next's shape), not
 *   react-router's `[params, setParams]` tuple. Every call site in this repo
 *   only ever READS (`searchParams?.get(...)`), so the setter is not needed;
 *   use `useSetSearchParams()` when it is.
 * - `router.refresh()` is a no-op. It exists to re-run a Next server render;
 *   an SPA has no server render to re-run and refetching is TanStack Query's
 *   job (`queryClient.invalidateQueries`).
 */
import { useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useParams as useRouterParams,
  useSearchParams as useRouterSearchParams,
} from "react-router-dom";

export function usePathname(): string {
  return useLocation().pathname;
}

export function useSearchParams(): URLSearchParams {
  const [params] = useRouterSearchParams();
  return params;
}

/** The setter half of react-router's `useSearchParams`, for call sites that write. */
export function useSetSearchParams() {
  const [, setParams] = useRouterSearchParams();
  return setParams;
}

export function useParams<T extends Record<string, string | undefined>>(): T {
  return useRouterParams() as T;
}

/**
 * Next's `NavigateOptions`. Accepted and ignored: `scroll: false` suppresses
 * the App Router's scroll-to-top, which react-router does not do in the first
 * place, so honouring it would be a no-op anyway.
 */
export interface NavigateOptions {
  scroll?: boolean;
}

export interface AppRouterInstance {
  push(href: string, options?: NavigateOptions): void;
  replace(href: string, options?: NavigateOptions): void;
  back(): void;
  forward(): void;
  /** No-op — see the module note. */
  refresh(): void;
  /** No-op — the SPA has no server-side route cache to warm. */
  prefetch(href: string): void;
}

export function useRouter(): AppRouterInstance {
  const navigate = useNavigate();
  return useMemo<AppRouterInstance>(
    () => ({
      push: (href, _options) => {
        void _options;
        navigate(href);
      },
      replace: (href, _options) => {
        void _options;
        navigate(href, { replace: true });
      },
      back: () => navigate(-1),
      forward: () => navigate(1),
      refresh: () => {},
      prefetch: () => {},
    }),
    [navigate],
  );
}
